import type React from "react";

import { useTranslation } from "react-i18next";
import invariant from "ts-invariant";

import { usePutTherapistMutation } from "api/hooks/usePutTherapistMutation";
import { useProfileContext } from "contexts/ProfileContext";
import { MUISwitch } from "shared/atoms/MUI/MUISwitch";

import { ProfileRow } from "../shared/ProfileRow";

export const AppointmentScheduledNotificationSwitch: React.FC = () => {
  const { profile, setProfile } = useProfileContext();
  const { t } = useTranslation();
  const putTherapistMutation = usePutTherapistMutation();

  invariant(profile);

  // Note! The server store the "ignore"-value while we are displaying the
  // opposite to enable notifications / disable notifications.
  const isIgnoreEmail = Boolean(profile.therapist_profile.ignore_appointment_scheduled_email);
  const isEnabled = !isIgnoreEmail;

  const onSubmit = (ignoreAppointmentScheduledEmail: boolean) => {
    const therapistId = profile.id;
    const data = {
      therapist_profile: {
        ignore_appointment_scheduled_email: ignoreAppointmentScheduledEmail,
      },
    };

    // Optimistically update the profile for immediate UI feedback.
    // Then onSuccess we update the profile with "real" server state.
    setProfile({
      ...profile,
      therapist_profile: {
        ...profile.therapist_profile,
        ...data.therapist_profile,
      },
    });

    const variables = { data, therapistId };
    putTherapistMutation.mutate(variables, {
      onSuccess(response) {
        setProfile(response);
      },
    });
  };

  return (
    <ProfileRow
      label={t("therapist_settings.appointment_scheduled_notification_switch")}
      labelTestId="appointment-scheduled-email-header"
      value={
        <MUISwitch
          data-testid="appointment-scheduled-email-switch"
          checked={isEnabled}
          onChange={() => {
            onSubmit(!isIgnoreEmail);
          }}
        />
      }
      errorMessage={putTherapistMutation.isError ? t("errors.generic") : undefined}
      dataTestId="appointment-scheduled-email"
    />
  );
};
